/**
 * A bundle as the import hands it over (part 24b, tables O′, Q and MB): one piece per record, in the order of table O′,
 * each with its texts and binaries read, decoded and checked (tables T′, I′). The worker sends the pieces to the page a
 * batch at a time, and the page reads each again field by field with {@link readPiece} (Definition of Done, security).
 */
import type { ConflictOrigin, CommentOrigin, CommentRange, NoteKind, SnapshotPin, SnapshotReason } from '@cockatrice/storage';
import { isJson } from './json.ts';
import type { JsonValue } from './json.ts';
import {
  COMMENT_KEYS,
  commentFields,
  COPY_KEYS,
  copyFields,
  ENTRY_KEYS,
  entryFields,
  fieldsOf,
  ManifestError,
  NOTE_KEYS,
  NOTE_RECORD_KEYS,
  noteRecordFields,
  readImageRecord,
  readNoteKind,
  readStore,
  readTemplateLink,
  readTrashedField,
  SHARE_KEYS,
  SHARE_LINK_KEYS,
  shareLinkRecordFields,
  shareRecordFields,
  SNAPSHOT_KEYS,
  snapshotFields,
  V7_STORES,
} from './manifest.ts';
import type { Fields, ManifestImageRecord, ManifestNoteRecord, ManifestShare, ManifestShareLink, ManifestTemplate, V7Store } from './manifest.ts';

/** Q: the most a batch holds of strings and binaries (`pieceBytes`), unless one piece alone is more. */
export const BATCH_BYTES = 8 * 1024 * 1024;

/** Q: the most pieces a batch holds. */
export const BATCH_PIECES = 4096;

/** The versions of the format a bundle may have (part 24a, 16c, 19d, 23c, 19e, 38d, 38b, 38e, 15e). */
export type BundleVersion = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/** Every version read, in order. */
export const BUNDLE_VERSIONS: readonly BundleVersion[] = Object.freeze([1, 2, 3, 4, 5, 6, 7, 8, 9]);

/** O′: the book: its ID, record (null fields when the store held none, or refused it), and the bundle's device and version. */
export interface BookPiece {
  readonly type: 'book';
  readonly id: string;
  readonly createdAt: number | null;
  readonly metadata: string | null;
  readonly theme: string | null;
  /** TL17–TL20: its template link (version 9), or null for none. */
  readonly template: ManifestTemplate | null;
  /** The device that made the bundle (decision 29: an import groups its snapshots by it). */
  readonly device: string;
  readonly version: BundleVersion;
}

/**
 * O′: a chapter, in the book (`trashed` null) or in the trash; its text null when the store couldn't read it (A9); its
 * notes a version 1 to 4 bundle's (empty in version 5, whose notes are note pieces: MB1, MB2); its subtitle a version 9
 * bundle's (empty before: TL20).
 */
export interface ChapterPiece {
  readonly type: 'chapter';
  readonly id: string;
  readonly title: string;
  readonly position: string;
  readonly synopsis: string;
  readonly subtitle: string;
  /** T‴2: a version 9 bundle's; empty before. */
  readonly image: string;
  readonly notes: string;
  readonly revision: string | null;
  readonly text: string | null;
  readonly trashed: { readonly at: number; readonly device: string } | null;
}

/** O′: a conflict copy. */
export interface CopyPiece {
  readonly type: 'copy';
  readonly id: string;
  readonly chapterId: string;
  readonly createdAt: number;
  readonly origin: ConflictOrigin;
  readonly text: string;
}

/** O′: a snapshot; its text null when the store held none or couldn't read it (A14, A15). */
export interface SnapshotPiece {
  readonly type: 'snapshot';
  readonly id: string;
  readonly chapterId: string;
  readonly owner: string;
  readonly seq: number;
  readonly createdAt: number;
  readonly settled: number | null;
  readonly zone: number;
  readonly reason: SnapshotReason;
  readonly pins: readonly SnapshotPin[];
  readonly text: string | null;
}

/** Table I′: an image's bytes agree with its record (`kept`), are found without one that agrees (`found`), or are missing. */
export type ImageState = 'kept' | 'found' | 'missing';

/** Every image state. */
export const IMAGE_STATES: readonly ImageState[] = Object.freeze(['kept', 'found', 'missing']);

/**
 * O′, I′: an image: its record as the manifest gives it (null for none), its bytes (null for none), and whether they
 * agree. A `found` image's record and bytes are untrusted (principle 7).
 */
export interface ImagePiece {
  readonly type: 'image';
  readonly id: string;
  readonly record: ManifestImageRecord | null;
  readonly bytes: Uint8Array | null;
  readonly state: ImageState;
}

/** O′: a character or place note (versions 3 and 4; a Notebook note since part 19e, MB3). */
export interface BookNotePiece {
  readonly type: 'bookNote';
  readonly id: string;
  readonly kind: NoteKind;
  readonly name: string;
  readonly text: string;
  readonly createdAt: number;
}

/** O′: a comment record (version 4; a Word note since part 19e, MB4). */
export interface CommentPiece {
  readonly type: 'comment';
  readonly id: string;
  readonly chapterId: string;
  readonly sourceKey: string;
  readonly origin: CommentOrigin;
  readonly author: string;
  readonly createdAt: number | null;
  readonly storedAt: number;
  readonly range: CommentRange | null;
  readonly quote: string;
  readonly before: string;
  readonly after: string;
  readonly text: string;
}

/**
 * O′, V5-1, V6-1, BV1: a note record (versions 5 to 9): its fields as the manifest gives them (version 5's link null,
 * version 5 to 7's share part null), its text read.
 */
export interface NotePiece extends Omit<ManifestNoteRecord, 'text'> {
  readonly type: 'note';
  readonly text: string;
}

/** O′, V6-1: a reading link's record (versions 6 to 9). */
export interface ShareLinkPiece extends ManifestShareLink {
  readonly type: 'shareLink';
}

/** O′, BU, BV1: a share's record (versions 7 to 9; version 7's with no role keys). */
export interface SharePiece extends ManifestShare {
  readonly type: 'share';
}

/**
 * O′, principle 7: a record the store that made the bundle refused, as stored (`raw`, untrusted), with the text its key
 * names (R2, R5) and the binary it held (table H), or null.
 */
export interface RefusedPiece {
  readonly type: 'refused';
  readonly store: V7Store;
  readonly key: string | null;
  readonly raw: JsonValue;
  readonly text: string | null;
  readonly bytes: Uint8Array | null;
}

/** One piece of a bundle. */
export type BundlePiece = BookPiece | ChapterPiece | CopyPiece | SnapshotPiece | ImagePiece | BookNotePiece | CommentPiece | NotePiece | ShareLinkPiece | SharePiece | RefusedPiece;

/** Q1: what `value` weighs in a batch: two bytes a string unit, a binary's length, eight anything else, keys included. */
function weigh(value: unknown): number {
  if (typeof value === 'string') return 2 * value.length;
  if (value instanceof Uint8Array) return value.length;
  if (typeof value !== 'object' || value === null) return 8;
  let total = 0;
  for (const [key, item] of Object.entries(value)) total += 2 * key.length + weigh(item);
  return total;
}

/** Q1: a piece's weight in a batch: its strings and binaries, and 64 for the piece. */
export function pieceBytes(piece: BundlePiece): number {
  return 64 + weigh(piece);
}

/** A string or null, at `key`. */
function stringOrNull(fields: Fields, key: string): string | null {
  return fields.get(key) === null ? null : fields.string(key);
}

/** A `Uint8Array` or null, at `key`. */
function bytesOrNull(fields: Fields, key: string): Uint8Array | null {
  const value = fields.get(key);
  if (value === null) return null;
  if (!(value instanceof Uint8Array)) throw new ManifestError(fields.name(key), 'must be bytes');
  return value;
}

/** One of `known`, at `key`. */
function oneOf<T>(fields: Fields, key: string, known: readonly T[]): T {
  const value = known.find((each) => each === fields.get(key));
  if (value === undefined) throw new ManifestError(fields.name(key), 'must be a known value');
  return value;
}

/** MB6: each piece type's reader over its fields (the type's own field included). */
const READERS: { readonly [T in BundlePiece['type']]: readonly [readonly string[], (fields: Fields) => Extract<BundlePiece, { type: T }>] } = {
  book: [
    ['type', 'id', 'createdAt', 'metadata', 'theme', 'template', 'device', 'version'],
    (fields) => ({
      type: 'book',
      id: fields.id('id'),
      createdAt: fields.countOrNull('createdAt'),
      metadata: stringOrNull(fields, 'metadata'),
      theme: stringOrNull(fields, 'theme'),
      template: readTemplateLink(fields.get('template'), fields.name('template')),
      device: fields.id('device'),
      version: oneOf(fields, 'version', BUNDLE_VERSIONS),
    }),
  ],
  chapter: [
    ['type', ...ENTRY_KEYS, 'notes', 'text', 'trashed'],
    (fields) => ({
      type: 'chapter',
      ...entryFields(fields),
      notes: fields.string('notes'),
      text: stringOrNull(fields, 'text'),
      trashed: fields.get('trashed') === null ? null : readTrashedField(fields.get('trashed'), fields.name('trashed')),
    }),
  ],
  copy: [['type', ...COPY_KEYS], (fields) => ({ type: 'copy', ...copyFields(fields), text: fields.string('text') })],
  snapshot: [['type', ...SNAPSHOT_KEYS], (fields) => ({ type: 'snapshot', ...snapshotFields(fields), text: stringOrNull(fields, 'text') })],
  image: [
    ['type', 'id', 'record', 'bytes', 'state'],
    (fields) => {
      const record = fields.get('record') === null ? null : readImageRecord(fields.get('record'), fields.name('record'));
      const bytes = bytesOrNull(fields, 'bytes');
      const state = oneOf(fields, 'state', IMAGE_STATES);
      // I′1–I′4: kept has both, found its bytes, missing its record alone.
      const holds = state === 'kept' ? record !== null && bytes !== null : state === 'found' ? bytes !== null : record !== null && bytes === null;
      if (!holds) throw new ManifestError(fields.name('state'), 'must be what the record and bytes allow');
      return { type: 'image', id: fields.id('id'), record, bytes, state };
    },
  ],
  bookNote: [['type', ...NOTE_KEYS], (fields) => ({ type: 'bookNote', id: fields.id('id'), kind: readNoteKind(fields), name: fields.string('name'), text: fields.string('text'), createdAt: fields.count('createdAt') })],
  comment: [['type', ...COMMENT_KEYS], (fields) => ({ type: 'comment', ...commentFields(fields), text: fields.string('text') })],
  // V5-6, BV1: every rule of the store's reader, the text's (a tombstone's empty) and the share part's (NR8) included.
  note: [['type', ...NOTE_RECORD_KEYS], (fields) => ({ type: 'note', ...noteRecordFields(fields, fields.string('text')), text: fields.string('text') })],
  // V6-1: every rule of the store's reader (table SR).
  shareLink: [['type', ...SHARE_LINK_KEYS], (fields) => ({ type: 'shareLink', ...shareLinkRecordFields(fields) })],
  // BU, BV1: every rule of the store's reader (table SH), the role keys' (SH5) included.
  share: [['type', ...SHARE_KEYS], (fields) => ({ type: 'share', ...shareRecordFields(fields) })],
  refused: [
    ['type', 'store', 'key', 'raw', 'text', 'bytes'],
    (fields) => {
      const raw = fields.get('raw');
      if (!isJson(raw)) throw new ManifestError(fields.name('raw'), 'must be a JSON value');
      return { type: 'refused', store: readStore(fields, V7_STORES), key: stringOrNull(fields, 'key'), raw, text: stringOrNull(fields, 'text'), bytes: bytesOrNull(fields, 'bytes') };
    },
  ],
};

/** MB6: whether `type` names a piece type. */
function isPieceType(type: unknown): type is BundlePiece['type'] {
  return typeof type === 'string' && Object.hasOwn(READERS, type);
}

/** MB6: the piece `value` is, read field by field, or null for anything else. */
export function readPiece(value: unknown): BundlePiece | null {
  const type: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'type') : undefined;
  if (!isPieceType(type)) return null;
  const [keys, read] = READERS[type];
  try {
    return read(fieldsOf(value, '', keys));
  } catch {
    // Every reader throws a ManifestError, and only for a field that isn't one.
    return null;
  }
}
